/**
 * Generated by orval v7.13.0 🍺
 * Do not edit manually.
 * Marimo Gateway API
 * API for managing marimo kernel sessions and proxying kernel requests
 * OpenAPI spec version: 0.0.0
 */
import type {
  AddPackageRequest,
  GetHealth200,
  MarimoCellOutput,
  MarimoKernelDelete200,
  MarimoKernelDeleteBody,
  MarimoKernelRun200,
  MarimoKernelRunBody,
  MarimoKernelSyncGraph200,
  MarimoKernelSyncGraphBody,
  MarimoUsage,
  PackageOperationResponse,
  PackageTreeResponse,
  RemovePackageRequest,
  SessionResponse,
  StopMarimoSession200,
} from "./marimo-api.generated.schemas";

import { client } from "./client";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Check if the service is running
 * @summary Health check
 */
export const getHealth = (options?: SecondParameter<typeof client<GetHealth200>>) => {
  return client<GetHealth200>({ url: `/marimo-api/health`, method: "GET" }, options);
};

/**
 * Get the current session state without starting a session
 * @summary Get marimo session status
 */
export const getMarimoSession = (
  documentId: string,
  options?: SecondParameter<typeof client<SessionResponse>>,
) => {
  return client<SessionResponse>(
    { url: `/marimo-api/sessions/${documentId}`, method: "GET" },
    options,
  );
};

/**
 * Start (or resume) a marimo session bound to a document
 * @summary Start a marimo session
 */
export const startMarimoSession = (
  documentId: string,
  options?: SecondParameter<typeof client<SessionResponse>>,
) => {
  return client<SessionResponse>(
    { url: `/marimo-api/sessions/${documentId}`, method: "POST" },
    options,
  );
};

/**
 * @summary Stop a marimo session
 */
export const stopMarimoSession = (
  documentId: string,
  options?: SecondParameter<typeof client<StopMarimoSession200>>,
) => {
  return client<StopMarimoSession200>(
    { url: `/marimo-api/sessions/${documentId}`, method: "DELETE" },
    options,
  );
};

/**
 * Submit one or more cells for execution in the session's kernel.
Proxied through to the marimo kernel. Cell output is streamed over
the session's WebSocket, not returned in the HTTP response.

 * @summary Run code in the marimo kernel
 */
export const marimoKernelRun = (
  sessionId: string,
  marimoKernelRunBody: MarimoKernelRunBody,
  options?: SecondParameter<typeof client<MarimoKernelRun200>>,
) => {
  return client<MarimoKernelRun200>(
    {
      url: `/marimo-api/kernel/${sessionId}/run`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: marimoKernelRunBody,
    },
    options,
  );
};

/**
 * In one atomic operation the kernel deletes cells absent from the
snapshot, registers new or changed cells, and runs the requested ones.

 * @summary Reconcile the kernel graph with the document and run cells
 */
export const marimoKernelSyncGraph = (
  sessionId: string,
  marimoKernelSyncGraphBody: MarimoKernelSyncGraphBody,
  options?: SecondParameter<typeof client<MarimoKernelSyncGraph200>>,
) => {
  return client<MarimoKernelSyncGraph200>(
    {
      url: `/marimo-api/kernel/${sessionId}/sync_graph`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: marimoKernelSyncGraphBody,
    },
    options,
  );
};

/**
 * Remove a cell from the kernel's registry. Globals it defined are
garbage-collected and dependent cells re-evaluate. Use this when a
code block is removed from the document to avoid stale variables
blocking re-definitions in newly-added cells.

 * @summary Delete a cell from the marimo kernel
 */
export const marimoKernelDelete = (
  sessionId: string,
  marimoKernelDeleteBody: MarimoKernelDeleteBody,
  options?: SecondParameter<typeof client<MarimoKernelDelete200>>,
) => {
  return client<MarimoKernelDelete200>(
    {
      url: `/marimo-api/kernel/${sessionId}/delete`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: marimoKernelDeleteBody,
    },
    options,
  );
};

/**
 * Returns the most recent persisted output for a cell. The endpoint
responds with a 302 redirect to a (typically signed) object-storage
URL; HTTP clients that follow redirects automatically (e.g. axios,
fetch, browsers) will end up with the JSON body shown below.

 * @summary Get persisted cell output
 */
export const getCellOutput = (
  documentId: string,
  cellId: string,
  options?: SecondParameter<typeof client<MarimoCellOutput>>,
) => {
  return client<MarimoCellOutput>(
    { url: `/marimo-api/outputs/${documentId}/${cellId}`, method: "GET" },
    options,
  );
};

/**
 * @summary Get the dependency tree for the session's installed packages
 */
export const marimoPackagesTree = (
  sessionId: string,
  options?: SecondParameter<typeof client<PackageTreeResponse>>,
) => {
  return client<PackageTreeResponse>(
    { url: `/marimo-api/packages/${sessionId}/tree`, method: "GET" },
    options,
  );
};

/**
 * @summary Install a package into the session's kernel
 */
export const marimoPackagesAdd = (
  sessionId: string,
  addPackageRequest: AddPackageRequest,
  options?: SecondParameter<typeof client<PackageOperationResponse>>,
) => {
  return client<PackageOperationResponse>(
    {
      url: `/marimo-api/packages/${sessionId}/add`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: addPackageRequest,
    },
    options,
  );
};

/**
 * @summary Get current CPU / memory / GPU usage for the session's kernel
 */
export const marimoUsage = (
  sessionId: string,
  options?: SecondParameter<typeof client<MarimoUsage>>,
) => {
  return client<MarimoUsage>({ url: `/marimo-api/usage/${sessionId}`, method: "GET" }, options);
};

/**
 * @summary Uninstall a package from the session's kernel
 */
export const marimoPackagesRemove = (
  sessionId: string,
  removePackageRequest: RemovePackageRequest,
  options?: SecondParameter<typeof client<PackageOperationResponse>>,
) => {
  return client<PackageOperationResponse>(
    {
      url: `/marimo-api/packages/${sessionId}/remove`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: removePackageRequest,
    },
    options,
  );
};

export type GetHealthResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>;
export type GetMarimoSessionResult = NonNullable<Awaited<ReturnType<typeof getMarimoSession>>>;
export type StartMarimoSessionResult = NonNullable<Awaited<ReturnType<typeof startMarimoSession>>>;
export type StopMarimoSessionResult = NonNullable<Awaited<ReturnType<typeof stopMarimoSession>>>;
export type MarimoKernelRunResult = NonNullable<Awaited<ReturnType<typeof marimoKernelRun>>>;
export type MarimoKernelSyncGraphResult = NonNullable<
  Awaited<ReturnType<typeof marimoKernelSyncGraph>>
>;
export type MarimoKernelDeleteResult = NonNullable<Awaited<ReturnType<typeof marimoKernelDelete>>>;
export type GetCellOutputResult = NonNullable<Awaited<ReturnType<typeof getCellOutput>>>;
export type MarimoPackagesTreeResult = NonNullable<Awaited<ReturnType<typeof marimoPackagesTree>>>;
export type MarimoPackagesAddResult = NonNullable<Awaited<ReturnType<typeof marimoPackagesAdd>>>;
export type MarimoUsageResult = NonNullable<Awaited<ReturnType<typeof marimoUsage>>>;
export type MarimoPackagesRemoveResult = NonNullable<
  Awaited<ReturnType<typeof marimoPackagesRemove>>
>;
